/**
 * Phone Number Validation and Formatting Utilities
 *
 * These utilities ensure consistent phone number handling across the application,
 * particularly for Supabase Auth OTP verification which requires E.164 format.
 *
 * E.164 Format: +[country code][subscriber number]
 * Example: +18676896780
 *
 * CRITICAL: Always use normalizeToE164() before sending phone to Supabase Auth
 */

/**
 * Normalize phone number to E.164 format
 * Ensures phone always has + prefix required by Supabase Auth
 *
 * @param phone - Phone number in any format
 * @returns Phone number in E.164 format with + prefix, or null if invalid
 *
 * @example
 * normalizeToE164('18676896780')   // Returns: '+18676896780'
 * normalizeToE164('+18676896780')  // Returns: '+18676896780'
 * normalizeToE164(null)            // Returns: null
 */
export function normalizeToE164(phone: string | null | undefined): string | null {
  if (!phone) return null;

  // Remove all whitespace and formatting characters
  const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');

  // Already in E.164 format
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // Add + prefix to numeric-only phone numbers
  if (/^\d+$/.test(cleaned)) {
    return `+${cleaned}`;
  }

  // Invalid format
  console.warn('[PhoneValidation] Invalid phone format:', phone);
  return null;
}

/**
 * Validate if phone number is in valid E.164 format
 *
 * @param phone - Phone number to validate
 * @returns true if valid E.164 format, false otherwise
 *
 * @example
 * isValidE164('+18676896780')  // Returns: true
 * isValidE164('18676896780')   // Returns: false (missing +)
 * isValidE164('+1-867-689-6780') // Returns: false (contains formatting)
 */
export function isValidE164(phone: string | null | undefined): boolean {
  if (!phone) return false;

  // E.164 format: + followed by 1-15 digits
  const e164Regex = /^\+\d{1,15}$/;
  return e164Regex.test(phone);
}

/**
 * Mask phone number for display purposes
 * Shows first few and last few digits, masks middle digits
 *
 * @param phone - Phone number to mask (preferably in E.164 format)
 * @returns Masked phone number for safe display
 *
 * @example
 * maskPhone('+18676896780')  // Returns: '+1••••6780'
 * maskPhone('18676896780')   // Returns: '18••••6780'
 */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return '';

  // For E.164 format (+1234567890)
  if (phone.startsWith('+')) {
    // Show country code and last 4 digits
    const match = phone.match(/^(\+\d{1,3})\d+(\d{4})$/);
    if (match) {
      return `${match[1]}••••${match[2]}`;
    }
  }

  // For non-E.164 format (1234567890)
  const match = phone.match(/^(\d{1,3})\d+(\d{4})$/);
  if (match) {
    return `${match[1]}••••${match[2]}`;
  }

  // Fallback: just show last 4 digits
  if (phone.length >= 4) {
    return `••••${phone.slice(-4)}`;
  }

  return '••••';
}

/**
 * Compare two phone numbers for equality, accounting for format differences
 *
 * @param phone1 - First phone number
 * @param phone2 - Second phone number
 * @returns true if phones are the same (ignoring formatting)
 *
 * @example
 * arePhoneNumbersEqual('+18676896780', '18676896780')  // Returns: true
 * arePhoneNumbersEqual('+1 (867) 689-6780', '+18676896780')  // Returns: true
 */
export function arePhoneNumbersEqual(
  phone1: string | null | undefined,
  phone2: string | null | undefined
): boolean {
  if (!phone1 || !phone2) return false;

  const normalized1 = normalizeToE164(phone1);
  const normalized2 = normalizeToE164(phone2);

  return normalized1 === normalized2;
}

/**
 * Extract country code from E.164 phone number
 *
 * @param phone - Phone number in E.164 format
 * @returns Country code (e.g., '1' for US/Canada) or null
 *
 * @example
 * getCountryCode('+18676896780')  // Returns: '1'
 * getCountryCode('+442071234567') // Returns: '44'
 */
export function getCountryCode(phone: string | null | undefined): string | null {
  if (!phone || !phone.startsWith('+')) return null;

  // Common country codes (1-3 digits)
  const match = phone.match(/^\+(\d{1,3})/);
  return match ? match[1] : null;
}

/**
 * Log phone format details for debugging
 * Helps track down phone format issues in OTP flow
 *
 * @param context - Description of where this is being called from
 * @param phone - Phone number to log
 * @param additionalInfo - Any additional context
 */
export function logPhoneFormat(
  context: string,
  phone: string | null | undefined,
  additionalInfo?: Record<string, any>
): void {
  if (!phone) {
    console.log(`[PhoneFormat] ${context}: No phone number provided`, additionalInfo);
    return;
  }

  const isE164 = isValidE164(phone);
  const normalized = normalizeToE164(phone);
  const countryCode = getCountryCode(normalized);

  console.log(`[PhoneFormat] ${context}:`, {
    original: phone,
    isE164Valid: isE164,
    normalized: normalized,
    masked: maskPhone(phone),
    countryCode: countryCode,
    ...additionalInfo,
  });
}
